import { EOModal, EOIcon } from '@nx/ui/eo';
import { useI18n } from '@nx/i18n';
import {
  detectConflicts,
  durationMinutes,
  findConflictWith,
  type Activity,
  type ActivityType,
  type Event,
  type EventDay,
} from '@nx/domain';
import { useMemo, useState } from 'react';

export function ActivityModal({
  open,
  mode,
  event: _event,
  days,
  dayId,
  initial,
  activities,
  onClose,
  onSave,
}: {
  open: boolean;
  mode: 'new' | 'edit';
  event: Event;
  days: EventDay[];
  dayId: string;
  initial?: Activity;
  activities: Activity[];
  onClose: () => void;
  onSave: (input: {
    date: string;
    title: string;
    type: ActivityType;
    startTime: string;
    endTime: string;
    venue: string;
    sortOrder: number;
  }) => void;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [d, setD] = useState(initial?.date ?? days.find((x) => x.id === dayId)?.date ?? '');
  const [s, setS] = useState(initial?.startTime ?? '09:00');
  const [e, setE] = useState(initial?.endTime ?? '10:00');
  const [venue, setVenue] = useState(initial?.venue ?? '');
  const [type, setType] = useState<ActivityType>(initial?.type ?? 'workshop');

  const dayActivities = activities.filter((a) => a.date === d && a.id !== initial?.id);
  const conflictWith = useMemo(() => {
    if (!venue) return null;
    return findConflictWith(
      { id: initial?.id ?? 'new', venue, startTime: s, endTime: e },
      dayActivities,
    );
  }, [d, s, e, venue, dayActivities, initial?.id]);

  if (!open) return null;

  return (
    <EOModal
      open
      title={mode === 'new' ? t('eo.agenda.addActivity') : t('eo.agenda.editActivity')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          {conflictWith ? (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() =>
                onSave({
                  date: d,
                  title,
                  type,
                  startTime: s,
                  endTime: e,
                  venue,
                  sortOrder: initial?.sortOrder ?? activities.length,
                })
              }
            >
              {t('eo.agenda.ignoreSave')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              onSave({
                date: d,
                title,
                type,
                startTime: s,
                endTime: e,
                venue,
                sortOrder: initial?.sortOrder ?? activities.length,
              })
            }
          >
            {conflictWith ? t('eo.agenda.resolveConflict') : t('common.submit')}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="field">
          <label>{t('eo.agenda.activityTitle')} *</label>
          <input className="input" value={title} onChange={(ev) => setTitle(ev.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div className="field">
            <label>{t('eo.agenda.date')}</label>
            <select className="select" value={d} onChange={(ev) => setD(ev.target.value)}>
              {days.map((x) => (
                <option key={x.id} value={x.date}>
                  {x.label} · {x.date}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>{t('eo.agenda.start')}</label>
            <input className="input" type="time" value={s} onChange={(ev) => setS(ev.target.value)} />
          </div>
          <div className="field">
            <label>{t('eo.agenda.end')}</label>
            <input className="input" type="time" value={e} onChange={(ev) => setE(ev.target.value)} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12 }}>
          <div className="field">
            <label>{t('eo.agenda.venue')}</label>
            <input className="input" value={venue} onChange={(ev) => setVenue(ev.target.value)} />
          </div>
          <div className="field">
            <label>{t('eo.agenda.type')}</label>
            <select
              className="select"
              value={type}
              onChange={(ev) => setType(ev.target.value as ActivityType)}
            >
              {(['talk', 'workshop', 'roundtable', 'break', 'meal', 'other'] as ActivityType[]).map(
                (v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ),
              )}
            </select>
          </div>
        </div>
        {conflictWith ? (
          <div className="conflict-banner">
            <EOIcon n="alert" size={18} />
            <div>
              <strong>{t('eo.agenda.conflictTitle')}</strong>
              <p>
                Overlaps with “{dayActivities.find((a) => a.id === conflictWith.id)?.title ?? conflictWith.id}” in{' '}
                {conflictWith.venue} from {conflictWith.startTime}–{conflictWith.endTime}.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </EOModal>
  );
}

export function ExportModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useI18n();
  const [fmt, setFmt] = useState('pdf');
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  return (
    <EOModal
      open
      title={t('eo.agenda.exportTitle')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setTimeout(() => {
                setBusy(false);
                onDone();
              }, 900);
            }}
          >
            {busy ? t('eo.agenda.generating') : t('eo.agenda.startExport')}
          </button>
        </>
      }
    >
      <ChoiceRow label={t('eo.agenda.format')} value={fmt} onChange={setFmt} options={[{ v: 'pdf', l: 'PDF' }, { v: 'png', l: 'Image' }]} />
    </EOModal>
  );
}

export function ShareModal({
  open,
  event,
  link,
  onClose,
  onPublicView,
  onEnable,
  onDisable,
}: {
  open: boolean;
  event: Event;
  link: string;
  onClose: () => void;
  onPublicView: () => void;
  onEnable: () => void;
  onDisable: () => void;
}) {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(Boolean(event.publicAgendaToken));
  const [copied, setCopied] = useState(false);
  if (!open) return null;
  const displayLink = link || `${window.location.origin}/p/…`;
  return (
    <EOModal
      open
      title={t('eo.agenda.shareTitle')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-outline" onClick={onPublicView}>
            <EOIcon n="eye" size={14} />
            {t('eo.agenda.previewPublic')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              await navigator.clipboard.writeText(displayLink);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
          >
            {copied ? t('eo.agenda.copied') : t('eo.agenda.copyLink')}
          </button>
        </>
      }
    >
      <p className="muted-text" style={{ marginTop: 0 }}>
        {t('eo.agenda.shareHint')}
      </p>
      {!link ? (
        <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={onEnable}>
          {t('eo.agenda.generateLink')}
        </button>
      ) : (
        <div className="copy-pill" style={{ width: '100%', justifyContent: 'space-between', marginTop: 12 }}>
          <code>{displayLink}</code>
        </div>
      )}
      <div
        className="row-spread"
        style={{
          marginTop: 18,
          padding: '12px 14px',
          background: 'var(--background-soft)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
        }}
      >
        <div>
          <strong style={{ fontFamily: 'var(--font-sans)', fontSize: 14 }}>{t('eo.agenda.linkStatus')}</strong>
        </div>
        <button
          type="button"
          className={`btn ${enabled ? 'btn-outline' : 'btn-primary'}`}
          onClick={() => {
            if (enabled) {
              onDisable();
              setEnabled(false);
            } else {
              onEnable();
              setEnabled(true);
            }
          }}
        >
          {enabled ? t('eo.agenda.disableLink') : t('eo.agenda.enableLink')}
        </button>
      </div>
    </EOModal>
  );
}

function ChoiceRow({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { v: string; l: string }[];
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            className={`btn ${value === o.v ? 'btn-primary' : 'btn-outline'} btn-sm`}
            onClick={() => onChange(o.v)}
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

export function TimelineView({
  activities,
  conflictIds,
  onPick,
}: {
  activities: Activity[];
  conflictIds: Set<string>;
  onPick: (a: Activity) => void;
}) {
  const startHour = 7;
  const endHour = 22;
  const hours = endHour - startHour + 1;
  const rowH = 56;
  const top = (mins: number) => ((mins - startHour * 60) / 60) * rowH;

  const toMin = (hhmm: string) => {
    const [h, m] = hhmm.split(':').map(Number);
    return (h ?? 0) * 60 + (m ?? 0);
  };

  const venues = [...new Set(activities.map((a) => a.venue ?? '—'))];
  const venueCol = Object.fromEntries(venues.map((v, i) => [v, i]));
  const cols = Math.max(1, venues.length);

  return (
    <div className="timeline" style={{ minHeight: hours * rowH + 24 }}>
      {Array.from({ length: hours }).map((_, i) => {
        const h = startHour + i;
        return (
          <span key={i}>
            <span className="tl-hour" style={{ top: i * rowH + 18 }}>
              {String(h).padStart(2, '0')}:00
            </span>
            <div className="tl-row major" style={{ top: i * rowH + 18 }} />
          </span>
        );
      })}
      <div style={{ position: 'relative', marginTop: 18, height: hours * rowH }}>
        {activities.map((a) => {
          const t = top(toMin(a.startTime));
          const h = ((toMin(a.endTime) - toMin(a.startTime)) / 60) * rowH;
          const col = venueCol[a.venue ?? '—'] ?? 0;
          const width = `calc((100% - ${(cols - 1) * 8}px) / ${cols})`;
          const left = `calc((${col} * (100% - ${(cols - 1) * 8}px) / ${cols}) + ${col * 8}px)`;
          const isConf = conflictIds.has(a.id);
          return (
            <div
              key={a.id}
              role="button"
              tabIndex={0}
              className={`tl-block ${isConf ? 'conflict' : ''}`}
              style={{ top: t, height: Math.max(34, h - 4), left, width }}
              onClick={() => onPick(a)}
              onKeyDown={(ev) => ev.key === 'Enter' && onPick(a)}
            >
              <strong>{a.title}</strong>
              <span className="t">
                {a.startTime} – {a.endTime} · {(a.venue ?? '').split('·')[0]?.trim()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export { detectConflicts, durationMinutes };
